<script setup lang="ts">
import { computed, ref, useId } from "vue";
import { ChevronDown, Circle, Droplets } from "@lucide/vue";
import type { ObservedRainWindow } from "rainowls-shared";
import type { TemperatureUnit } from "@/stores/settings.store";
import { useUnitConversion } from "@/composables/useUnitConversion";
import LoadingValue from "@/components/ui/LoadingValue.vue";
import { Skeleton } from "@/components/ui/skeleton";
import { rainIntensity, validRain } from "@/utils/precipitationDisplay";

const props = defineProps<{
  wallMode?: boolean;
  loading?: boolean;
  unit: TemperatureUnit;
  rate?: number | null;
  today?: number | null;
  storm?: number | null;
  forecastStorm?: number | null;
  observations?: ObservedRainWindow | null;
  timeZone?: string | null;
}>();

const { convertPrecipitation, labels } = useUnitConversion(() => props.unit);
const selectedHour = ref<number | null>(null);
const hoursExpanded = ref(false);
const hoursId = useId();
const intensity = computed(() => rainIntensity(props.rate));
// Wall displays prioritize the three observed readings; the model stays a separate comparison below.
const compactReadings = computed(() => [
  {
    label: "Storm",
    description: "Observed storm total",
    value: props.storm,
    unit: labels.value.precipitation,
    color: "var(--rain-text)",
  },
  {
    label: "Today",
    description: "Today",
    value: props.today,
    unit: labels.value.precipitation,
    color: "var(--rain-text)",
  },
  {
    label: "Rate",
    description: "Rain rate",
    value: props.rate,
    unit: `${labels.value.precipitation}/hr`,
    color: intensity.value.color,
  },
]);
const description = computed(() => {
  if (intensity.value.label === "Unavailable") return "Rain rate unavailable";
  return intensity.value.label === "Dry"
    ? "Dry"
    : `${intensity.value.label} rain`;
});
const hours = computed(() => props.observations?.hours ?? []);
const chartScale = computed(() =>
  Math.max(0, ...hours.value.map((hour) => hour.amount ?? 0)),
);
const hasHourlyData = computed(() =>
  hours.value.some((hour) => hour.amount !== null),
);
const hourFormatter = computed(() => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      timeZone: props.timeZone || undefined,
    });
  } catch {
    return null;
  }
});

/** Convert millimetres for display while keeping tiny readings, measured zero and missing data distinct. */
function amountText(amount: number | null | undefined): string {
  const measurement = validRain(amount);
  const value = convertPrecipitation(measurement);
  if (value === null) return "—";
  if (value === 0 && measurement !== null && measurement > 0) return "<0.01";
  return value.toFixed(2);
}

/** Format a prepared hourly interval end in the station's local time zone. */
function hourText(index: number): string {
  const hour = hours.value[index];
  return hour && hourFormatter.value
    ? hourFormatter.value.format(hour.time)
    : "—";
}

/** Describe hourly amounts and distinguish the wettest hour from an instantaneous peak rate. */
function hourLabel(index: number): string {
  const hour = hours.value[index];
  if (!hour) return "Hourly rain unavailable";
  const parts = [
    `${hourText(index)}: ${amountText(hour.amount)} ${labels.value.precipitation} rain`,
  ];
  if (index === props.observations?.peakHour) parts.push("peak hour");
  if (index === props.observations?.peakRateHour) {
    parts.push(
      `peak rate ${amountText(props.observations.peakRate)} ${labels.value.precipitation} per hour`,
    );
  }
  return parts.join(", ");
}

/** Scale only measured hourly amounts; a short neutral tick represents a measured zero. */
function barHeight(amount: number | null): string {
  if (amount === null) return "0%";
  if (amount === 0) return "3px";
  return `${Math.max(8, chartScale.value ? (amount / chartScale.value) * 100 : 0)}%`;
}

/** Pin an hour's reading for touch input; tapping it again dismisses the tooltip. */
function toggleHour(index: number): void {
  selectedHour.value = selectedHour.value === index ? null : index;
}
</script>

<template>
  <section
    class="@container min-w-0 tabular-nums"
    aria-label="Rain observations"
    @keydown.esc="selectedHour = null"
  >
    <div
      class="flex min-w-0 items-center justify-between gap-3"
      :class="wallMode ? 'mb-2' : 'mb-4'"
    >
      <h3
        class="flex items-center gap-2 text-base font-semibold text-text-primary"
      >
        <Droplets
          class="size-5 shrink-0 text-[var(--rain-text)]"
          aria-hidden="true"
        />
        Rain
      </h3>
      <p
        class="flex min-w-0 items-center gap-1.5 text-right text-xs text-text-muted"
        aria-live="polite"
      >
        <LoadingValue :loading="loading">
          <span class="inline-flex items-center gap-1.5">
            <Circle
              class="size-1.5 shrink-0 fill-current"
              :style="{ color: intensity.color }"
              aria-hidden="true"
            />
            {{ loading ? "Light rain" : description }}
          </span>
        </LoadingValue>
      </p>
    </div>

    <div v-if="wallMode">
      <dl class="grid grid-cols-3 gap-3">
        <div
          v-for="reading in compactReadings"
          :key="reading.label"
          class="min-w-0"
          :aria-label="
            loading
              ? undefined
              : `${reading.description}: ${amountText(reading.value)} ${reading.unit === labels.precipitation ? reading.unit : labels.precipitation + ' per hour'}`
          "
        >
          <dt class="text-xs leading-4 text-text-muted">{{ reading.label }}</dt>
          <dd
            class="mt-1 flex min-h-10 flex-wrap content-start items-baseline gap-x-1"
            :style="{ color: reading.color }"
          >
            <strong
              class="text-[22px] leading-none font-semibold tracking-tight"
            >
              <LoadingValue :loading="loading">{{
                loading ? "0.00" : amountText(reading.value)
              }}</LoadingValue>
            </strong>
            <span class="text-[10px] leading-4 text-text-muted">{{
              reading.unit
            }}</span>
          </dd>
        </div>
      </dl>
      <p
        class="mt-2 flex flex-wrap items-baseline gap-x-2 text-[11px] leading-4 text-text-muted"
        :aria-label="
          loading
            ? undefined
            : `Modeled event total: ${amountText(forecastStorm)} ${labels.precipitation}`
        "
      >
        <span>Modeled event total</span>
        <LoadingValue
          :loading="loading"
          class="font-semibold text-[var(--pop-ink)]"
          >{{ loading ? "0.00" : amountText(forecastStorm) }}
          {{ labels.precipitation }}</LoadingValue
        >
        <span class="sr-only">Station and model event windows may differ.</span>
      </p>
    </div>
    <template v-else>
      <div
        class="grid min-w-0 grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)] gap-4 @min-[520px]:gap-7"
      >
        <div
          class="min-w-0 border-r border-[var(--conditions-divider)] pr-4 @min-[520px]:pr-7"
        >
          <p class="text-xs leading-4 text-text-muted @min-[520px]:text-sm">
            Observed storm total
          </p>
          <!-- Reserve a unit line even when it fits inline, so longer metric totals cannot resize the header. -->
          <p
            class="mt-1.5 flex min-h-[calc(clamp(28px,10cqw,72px)+20px)] flex-wrap content-start items-baseline gap-x-1.5 text-[var(--rain-text)]"
            :aria-label="
              loading
                ? undefined
                : `Observed storm total: ${amountText(storm)} ${labels.precipitation}`
            "
          >
            <strong
              class="min-w-0 text-[clamp(28px,10cqw,72px)] leading-none font-semibold tracking-[-0.045em]"
              ><LoadingValue :loading="loading">{{
                loading ? "0.00" : amountText(storm)
              }}</LoadingValue></strong
            >
            <span class="text-sm text-text-muted">{{
              labels.precipitation
            }}</span>
          </p>
          <div
            class="mt-4 border-t-2 border-dotted border-[var(--pop-ink)] pt-2.5"
          >
            <p class="text-[11px] leading-4 text-text-muted">
              Modeled event total
            </p>
            <p
              class="mt-1 flex min-h-[calc(clamp(18px,3.5cqw,26px)+16px)] flex-wrap content-start items-baseline gap-x-1 text-[var(--pop-ink)]"
              :aria-label="
                loading
                  ? undefined
                  : `Modeled event total: ${amountText(forecastStorm)} ${labels.precipitation}`
              "
            >
              <strong
                class="text-[clamp(18px,3.5cqw,26px)] leading-none font-semibold tracking-[-0.03em]"
                ><LoadingValue :loading="loading">{{
                  loading ? "0.00" : amountText(forecastStorm)
                }}</LoadingValue></strong
              >
              <span class="text-xs text-text-muted">{{
                labels.precipitation
              }}</span>
            </p>
            <p class="mt-2 text-[10px] leading-4 text-text-muted">
              Station and model event windows may differ.
            </p>
          </div>
        </div>

        <div class="min-w-0">
          <dl class="divide-y divide-[var(--conditions-divider)]">
            <div
              class="flex min-w-0 flex-wrap items-start justify-between gap-x-2 gap-y-1 pb-3"
              :aria-label="
                loading
                  ? undefined
                  : `Today: ${amountText(today)} ${labels.precipitation}`
              "
            >
              <dt class="text-sm font-medium text-text-primary">
                Today<span
                  class="mt-0.5 block text-[10px] font-normal text-text-muted"
                  >Since midnight</span
                >
              </dt>
              <dd
                class="flex flex-wrap items-baseline gap-x-1 text-[var(--rain-text)]"
              >
                <strong
                  class="text-[clamp(20px,5cqw,32px)] leading-none font-semibold tracking-[-0.03em]"
                  ><LoadingValue :loading="loading">{{
                    loading ? "0.00" : amountText(today)
                  }}</LoadingValue></strong
                ><span class="text-[11px] text-text-muted">{{
                  labels.precipitation
                }}</span>
              </dd>
            </div>
            <div
              class="flex min-w-0 flex-wrap items-start justify-between gap-x-2 gap-y-1 py-3"
              :aria-label="
                loading
                  ? undefined
                  : `Rain rate: ${amountText(rate)} ${labels.precipitation} per hour`
              "
            >
              <dt class="text-sm font-medium text-text-primary">Rain rate</dt>
              <dd
                class="flex flex-wrap items-baseline gap-x-1"
                :style="{ color: intensity.color }"
              >
                <strong
                  class="text-[clamp(20px,5cqw,32px)] leading-none font-semibold tracking-[-0.03em]"
                  ><LoadingValue :loading="loading">{{
                    loading ? "0.00" : amountText(rate)
                  }}</LoadingValue></strong
                ><span class="text-[11px] text-text-muted"
                  >{{ labels.precipitation }}/hr</span
                >
              </dd>
            </div>
          </dl>

          <div class="border-t border-[var(--conditions-divider)] pt-1">
            <div
              class="flex min-h-9 flex-wrap items-center justify-between gap-x-2"
            >
              <h4 class="text-[11px] text-text-muted">Hourly observations</h4>
              <button
                v-if="hasHourlyData && !loading"
                type="button"
                class="group inline-flex min-h-9 items-center gap-1 text-[10px] text-text-muted hover:text-text-primary focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-accent"
                :aria-expanded="hoursExpanded"
                :aria-controls="hoursId"
                @click="hoursExpanded = !hoursExpanded"
              >
                Inspect hours
                <ChevronDown
                  class="size-3 transition-transform motion-reduce:transition-none"
                  :class="{ 'rotate-180': hoursExpanded }"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="mt-1">
              <Skeleton
                v-if="loading"
                class="h-10 w-full @min-[520px]:h-14"
                aria-hidden="true"
              />
              <div
                v-else-if="hasHourlyData"
                class="grid h-10 grid-cols-[repeat(24,minmax(0,1fr))] items-end gap-px border-b border-[var(--conditions-divider)] @min-[520px]:h-14"
                role="group"
                aria-label="Observed rain by hour over the past 24 hours"
              >
                <button
                  v-for="(hour, index) in hours"
                  :key="hour.time.getTime()"
                  type="button"
                  class="group relative flex h-full min-w-0 items-end focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                  :aria-label="hourLabel(index)"
                  :aria-pressed="selectedHour === index"
                  @click="toggleHour(index)"
                >
                  <span
                    class="w-full rounded-t-sm transition-[height] duration-150 motion-reduce:transition-none"
                    :class="
                      hour.amount === 0
                        ? 'bg-app-selected'
                        : 'bg-[var(--rain-text)]'
                    "
                    :style="{ height: barHeight(hour.amount) }"
                  />
                  <span
                    aria-hidden="true"
                    class="pointer-events-none absolute bottom-full z-10 mb-2 min-w-28 rounded-small border border-app-border bg-app-surface px-2 py-1.5 text-left text-[11px] leading-4 text-text-primary shadow-popover"
                    :class="[
                      selectedHour === index
                        ? 'block'
                        : 'hidden group-hover:block group-focus-visible:block',
                      index < 8 ? 'left-0' : 'right-0',
                    ]"
                  >
                    <span class="block font-medium whitespace-nowrap"
                      >{{ hourText(index) }} · {{ amountText(hour.amount) }}
                      {{ labels.precipitation }}</span
                    >
                    <span v-if="index === observations?.peakHour" class="block"
                      >Peak hour</span
                    >
                    <span
                      v-if="index === observations?.peakRateHour"
                      class="block"
                      >Peak rate {{ amountText(observations?.peakRate) }}
                      {{ labels.precipitation }}/hr</span
                    >
                  </span>
                </button>
              </div>
              <p
                v-else
                class="flex h-10 items-center text-[11px] leading-4 text-text-muted @min-[520px]:h-14"
              >
                Hourly observations unavailable
              </p>
              <div
                class="mt-1 flex justify-between text-[9px] text-text-muted"
                :aria-hidden="loading || !hasHourlyData || undefined"
              >
                <LoadingValue :loading="loading">{{
                  hasHourlyData ? hourText(0) : "—"
                }}</LoadingValue>
                <LoadingValue :loading="loading">{{
                  hasHourlyData ? hourText(12) : "—"
                }}</LoadingValue>
                <span>Now</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <dl
        v-if="hoursExpanded && hasHourlyData && !loading"
        :id="hoursId"
        class="mt-3 grid max-h-40 grid-cols-2 gap-x-5 gap-y-2 overflow-y-auto border-t border-[var(--conditions-divider)] pt-3 text-xs @min-[520px]:grid-cols-3"
        aria-label="Hourly rain readings"
      >
        <div
          v-for="(hour, index) in hours"
          :key="hour.time.getTime()"
          class="flex justify-between gap-2"
        >
          <dt class="text-text-muted">{{ hourText(index) }}</dt>
          <dd class="font-medium text-text-primary">
            {{ amountText(hour.amount) }} {{ labels.precipitation }}
          </dd>
        </div>
      </dl>
    </template>
  </section>
</template>
